import { useMemo, type PropsWithChildren } from 'react';

import {
  AuthProviderComponent,
  type AuthProvider as AuthProviderContract,
  type Session,
} from '@sshop/auth';
import type { AuthSession, User } from '@sshop/domain';

import { api } from '@/shared/lib/apiClient';
import { getAuthToken, setAuthToken } from '@/shared/lib/authToken';

function toSession(user: User): Session {
  return {
    userId: user.id,
    email: user.email,
    displayName: user.name || user.email,
  };
}

function fromAuthSession(authSession: AuthSession): Session {
  setAuthToken(authSession.token);
  return toSession(authSession.user);
}

export function AuthProvider({ children }: PropsWithChildren<object>) {
  const provider = useMemo<AuthProviderContract>(() => {
    const listeners = new Set<(s: Session | null) => void>();
    const notify = (s: Session | null) => {
      listeners.forEach((cb) => cb(s));
    };

    return {
      async getSession() {
        const initialToken = getAuthToken();
        if (!initialToken) return null;
        try {
          const user = await api.auth.me();
          const session = toSession(user);
          notify(session);
          return session;
        } catch {
          if (getAuthToken() === initialToken) setAuthToken(null);
          return null;
        }
      },
      async login(credentials) {
        const result = await api.auth.login({
          email: credentials.email,
          password: credentials.password,
        });
        const session = fromAuthSession(result);
        notify(session);
        return session;
      },
      async register(credentials) {
        const result = await api.auth.register({
          name: credentials.name,
          surname: credentials.surname,
          phone: credentials.phone,
          idNumber: credentials.idNumber,
          email: credentials.email,
          password: credentials.password,
          passwordRepeat: credentials.passwordRepeat,
          agree: credentials.agree,
        });
        const session = fromAuthSession(result);
        notify(session);
        return session;
      },
      async forgotPassword(credentials) {
        await api.auth.forgotPassword({ email: credentials.email });
      },
      async resetPassword(credentials) {
        await api.auth.resetPassword({
          token: credentials.token,
          email: credentials.email,
          password: credentials.password,
          passwordConfirmation: credentials.passwordConfirmation,
        });
      },
      async socialRedirectUrl(provider) {
        return api.auth.socialRedirectUrl(provider);
      },
      async logout() {
        try {
          await api.auth.logout();
        } catch {
          /* ignore network errors during logout */
        }
        setAuthToken(null);
        notify(null);
      },
      onSessionChange(cb) {
        listeners.add(cb);
        return () => {
          listeners.delete(cb);
        };
      },
    };
  }, []);

  return <AuthProviderComponent provider={provider}>{children}</AuthProviderComponent>;
}
